import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack, VStack, type StackGap } from '@components/base/layout';
import { Alert, Modal, Spinner } from '@components/base/feedback';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VariantStyle {
  gap: StackGap;
  titleAs: 'h1' | 'h3';
  titleSize: '2xl' | 'lg';
  titleWeight: 'bold' | 'semibold';
  paddingClass: string;
}

const VARIANT_STYLES: Record<'page' | 'panel', VariantStyle> = {
  page: { gap: 6, titleAs: 'h1', titleSize: '2xl', titleWeight: 'bold', paddingClass: 'py-16' },
  panel: { gap: 4, titleAs: 'h3', titleSize: 'lg', titleWeight: 'semibold', paddingClass: 'py-12' },
};

export interface AdminListPageProps<TItem> {
  /** Page/section title. */
  title: string;
  /** Optional subtitle shown beneath the title. */
  subtitle?: string;
  /** Label for the loading spinner, e.g. "Loading tenants...". */
  loadingLabel: string;
  /** Alert title shown on fetch error, e.g. "Failed to load tenants". */
  errorTitle: string;
  /** Fallback message when the fetch error is not an Error instance. */
  errorFallback: string;
  /** Whether to display the title even in the error state. */
  showTitleOnError?: boolean;

  // ---- Empty state ----
  /** Title for the empty-state alert, e.g. "No tenants found". */
  emptyTitle: string;
  /** Body text for the empty-state alert. */
  emptyDescription: string;

  // ---- Create button ----
  /** Label for the create button, e.g. "Create Tenant". */
  createLabel: string;
  /** Called when the create button is clicked. */
  onCreateClick: () => void;

  // ---- Table ----
  /** The list of items to render. */
  items: TItem[];
  /** React key extractor for table rows. */
  getRowKey: (item: TItem) => string | number;
  /** Table header cells (rendered inside `Table.Head > Table.Row`). */
  renderHead: () => ReactNode;
  /** Renders a single table row. */
  renderRow: (item: TItem) => ReactNode;
  /** Optional click handler called when a table row is clicked. */
  onRowClick?: (item: TItem) => void;

  // ---- Query state ----
  /** Whether the list is loading. */
  isLoading: boolean;
  /** Fetch error, if any. */
  error: Error | null;

  // ---- Delete modal ----
  /** Modal title, e.g. "Delete Tenant". */
  deleteModalTitle: string;
  /** The item currently targeted for deletion (null = modal closed). */
  deleteTarget: TItem | null;
  /** Renders the name of the delete target shown in the confirmation text. */
  getDeleteTargetName: (item: TItem) => string;
  /** Noun used in the confirmation sentence, e.g. "tenant". */
  deleteNoun: string;
  /** Called to close the delete modal. */
  onDeleteCancel: () => void;
  /** Called when the user confirms deletion. */
  onDeleteConfirm: () => void;
  /** Whether the delete mutation is in flight. */
  isDeleting: boolean;
  /** Whether the delete mutation errored. */
  isDeleteError?: boolean;
  /** The delete mutation error. */
  deleteError?: Error | null;

  // ---- Layout variants ----
  /**
   * Layout variant. "page" uses h1 with 2xl size and gap 6.
   * "panel" uses h3 with lg size and gap 4.
   * @default "page"
   */
  variant?: 'page' | 'panel';
  /**
   * Size for the create button. Panels typically use "sm".
   * @default undefined (uses Button's default "md")
   */
  createButtonSize?: 'sm' | 'md' | 'lg';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function renderErrorMessage(error: Error | null, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function PageHeader({
  title,
  subtitle,
  style,
}: Readonly<{ title: string; subtitle?: string; style: VariantStyle }>) {
  if (subtitle) {
    return (
      <VStack gap={1}>
        <Text as={style.titleAs} size={style.titleSize} weight={style.titleWeight}>
          {title}
        </Text>
        <Text size="sm" color="muted">
          {subtitle}
        </Text>
      </VStack>
    );
  }
  return (
    <Text as={style.titleAs} size={style.titleSize} weight={style.titleWeight}>
      {title}
    </Text>
  );
}

function DeleteModal<TItem>({
  deleteModalTitle,
  deleteTarget,
  getDeleteTargetName,
  deleteNoun,
  onDeleteCancel,
  onDeleteConfirm,
  isDeleting,
  isDeleteError,
  deleteError,
}: Readonly<{
  deleteModalTitle: string;
  deleteTarget: TItem | null;
  getDeleteTargetName: (item: TItem) => string;
  deleteNoun: string;
  onDeleteCancel: () => void;
  onDeleteConfirm: () => void;
  isDeleting: boolean;
  isDeleteError: boolean;
  deleteError: Error | null;
}>) {
  return (
    <Modal
      open={deleteTarget !== null}
      onClose={onDeleteCancel}
      size="sm"
    >
      <Modal.Header title={deleteModalTitle} />
      <Modal.Body>
        <VStack gap={3}>
          <Text>
            Are you sure you want to delete the {deleteNoun}{' '}
            <Text as="span" weight="bold">
              {deleteTarget ? getDeleteTargetName(deleteTarget) : ''}
            </Text>
            ? This action cannot be undone.
          </Text>
          {isDeleteError && (
            <Alert variant="error" title="Delete failed">
              {renderErrorMessage(deleteError, 'An unexpected error occurred.')}
            </Alert>
          )}
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button
            variant="secondary"
            onClick={onDeleteCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={isDeleting}
            onClick={onDeleteConfirm}
          >
            Delete
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminListPage<TItem>(props: Readonly<AdminListPageProps<TItem>>) {
  const {
    title,
    subtitle,
    loadingLabel,
    errorTitle,
    errorFallback,
    showTitleOnError = false,
    emptyTitle,
    emptyDescription,
    createLabel,
    onCreateClick,
    items,
    getRowKey,
    renderHead,
    renderRow,
    onRowClick,
    isLoading,
    error,
    deleteModalTitle,
    deleteTarget,
    getDeleteTargetName,
    deleteNoun,
    onDeleteCancel,
    onDeleteConfirm,
    isDeleting,
    isDeleteError = false,
    deleteError = null,
    variant = 'page',
    createButtonSize,
  } = props;

  const style = VARIANT_STYLES[variant];
  const iconSize = createButtonSize === 'sm' ? 'xs' as const : 'sm' as const;
  const iconColor = createButtonSize === 'sm' ? 'inherit' as const : undefined;

  // ---- Loading state ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className={style.paddingClass}>
        <Spinner size="lg" />
        <Text color="muted">{loadingLabel}</Text>
      </VStack>
    );
  }

  // ---- Error state ----

  if (error) {
    const errorContent = (
      <Alert variant="error" title={errorTitle}>
        {renderErrorMessage(error, errorFallback)}
      </Alert>
    );

    if (showTitleOnError) {
      return (
        <VStack gap={style.gap}>
          <Text as={style.titleAs} size={style.titleSize} weight={style.titleWeight}>
            {title}
          </Text>
          {errorContent}
        </VStack>
      );
    }

    return errorContent;
  }

  // ---- Main content ----

  return (
    <VStack gap={style.gap}>
      {/* Page header */}
      <HStack justify="between" align="center">
        <PageHeader title={title} subtitle={subtitle} style={style} />
        <Button
          variant="primary"
          size={createButtonSize}
          leftIcon={<Icon icon={Plus} size={iconSize} color={iconColor} />}
          onClick={onCreateClick}
        >
          {createLabel}
        </Button>
      </HStack>

      {/* Table or empty state */}
      {items.length === 0 ? (
        <Alert variant="info" title={emptyTitle}>
          {emptyDescription}
        </Alert>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>{renderHead()}</Table.Row>
          </Table.Head>
          <Table.Body>
            {items.map((item) => (
              <Table.Row key={getRowKey(item)} onClick={onRowClick ? () => onRowClick(item) : undefined}>{renderRow(item)}</Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Delete confirmation modal */}
      <DeleteModal
        deleteModalTitle={deleteModalTitle}
        deleteTarget={deleteTarget}
        getDeleteTargetName={getDeleteTargetName}
        deleteNoun={deleteNoun}
        onDeleteCancel={onDeleteCancel}
        onDeleteConfirm={onDeleteConfirm}
        isDeleting={isDeleting}
        isDeleteError={isDeleteError}
        deleteError={deleteError}
      />
    </VStack>
  );
}
